import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Grid,
  Tabs,
  Tab,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DeployIcon from "@mui/icons-material/FlightTakeoff";
import { DataGrid } from "@mui/x-data-grid";
import { getContent, listManifestItems } from "../../graphql/queries";
import { deleteManifestItem } from "../../graphql/mutations";
import { generateClient } from "aws-amplify/api";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { addDataToQueue } from "../../helper/addToQueue/add-queue-item";
import CustomToast from "../../components/customToast";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { defaultStorage } from "aws-amplify/utils";

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const ManifestItems = () => {
  const theme = createTheme({
    palette: {
      primary: {
        main: "#2196f3",
      },
      secondary: {
        main: "#f50057",
      },
      background: {
        default: "#f0f0f0",
      },
    },
    typography: {
      fontFamily: ['"Roboto"', "Arial", "sans-serif"].join(","),
      fontWeightBold: 600,
    },
  });
  const styles = {
    container: {
      padding: "20px",
      color: theme.palette.text.primary,
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "20px",
    },
    heading: {
      fontWeight: theme.typography.fontWeightBold,
      color: "black",
    },
    addItemButton: {
      textAlign: "right",
      marginBottom: "20px",
    },
    buttonAdd: {
      marginRight: "10px",
      color: theme.palette.primary.main,
    },
    buttonDeploy: {
      color: theme.palette.secondary.main,
    },
    tabsContainer: {
      width: "100%",
      marginBottom: "10px",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    },
    tab: {
      fontWeight: theme.typography.fontWeightBold,
      color: theme.palette.text.primary,
    },
    subTab: {
      fontWeight: theme.typography.fontWeightBold,
      backgroundColor: "#80868c",
    },
    loadingIndicator: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "200px",
    },
    noItemsMessage: {
      textAlign: "center",
      marginTop: "20px",
      fontSize: "16px",
      color: theme.palette.text.secondary,
    },
    dataGrid: {
      width: "100%",
      border: "none",
      borderRadius: "4px",
      backgroundColor: "#fff",
      "& .MuiDataGrid-cell:focus": {
        outline: "none",
      },
      "& .MuiDataGrid-columnHeader": {
        backgroundColor: theme.palette.primary.light,
      },
    },
  };
  const client = generateClient();
  const navigate = useNavigate();
  const location = useLocation();
  let manifestId = location.state.manifestId;
  const [manifestID, setManifestID] = useState(null);
  const [manifestItems, setManifestItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [subTabValue, setSubTabValue] = useState(0);
  const [showToast, setShowToast] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState("");
  const [toastType, setToastType] = React.useState("");
  const [accessToken, setAccessToken] = useState("");

  useEffect(() => {
    setManifestID(manifestId);
    if (manifestId) {
      fetchItems(manifestId);
    }
  }, [manifestID]);

  const handleOpenDialog = (item) => {
    navigate("/details", {
      state: {
        details: item,
      },
    });
  };

  const handleBackClick = () => {
    navigate(-1);
  };
  const fetchItems = async (id) => {
    const tokens = await cognitoUserPoolsTokenProvider.getTokens();
    console.log(tokens.accessToken.toString(), "tokens");
    setAccessToken(tokens.accessToken.toString());
    setLoading(true);
    try {
      const response = await client.graphql({
        query: listManifestItems,
      });

      const items = response.data.listManifestItems.items.filter(
        (item) => item.manifestID === id
      );
      if (items.length > 0) {
        const contentDetailsPromises = items.map((item) =>
          client
            .graphql({
              query: getContent,
              variables: { id: item.contentId },
            })
            .then((contentResponse) => ({
              ...item,
              contentDetails: contentResponse.data.getContent,
            }))
            .catch((error) => {
              console.error(
                "Error fetching content detail for item:",
                item,
                error
              );
              return null;
            })
        );

        let itemsWithContentDetails = await Promise.all(contentDetailsPromises);
        itemsWithContentDetails = itemsWithContentDetails.filter(
          (item) => item !== null
        );

        const transformedItems = itemsWithContentDetails.map((item) => {
          const { contentDetails, ...restOfItem } = item;

          return {
            ...restOfItem,
            title: contentDetails?.title,
            type: contentDetails?.type,
            category: contentDetails?.category,
            director: contentDetails?.director,
            language: contentDetails?.language,
            posters: contentDetails?.posters,
            filePath: contentDetails?.filePath,
            actors: contentDetails?.actors,
            genre: contentDetails?.genre,
          };
        });

        console.log(transformedItems, "transformed");
        setManifestItems(transformedItems);
      } else {
        setManifestItems([]);
      }
    } catch (error) {
      console.error("Error fetching Manifest contents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItemClick = (id) => {
    navigate("/additems", {
      state: {
        userId: id,
      },
    });
  };
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSubTabValue(0);
  };
  const handleDeployClick = async () => {
    try {
      await addDataToQueue(manifestId, accessToken);
      setToastType("success");
      setShowToast(true);
    } catch (error) {
      setToastMessage(error.message);
      setToastType("error");
      setShowToast(true);
    }
  };

  const handleSubTabChange = (event, newValue) => {
    setSubTabValue(newValue);
  };

  const getSubTabs = () => {
    switch (tabValue) {
      case 0:
        return [
          "Movies",
          "TV",
          "DreamKids",
          "DreamBites",
          "Documentaries",
          "Sports",
          "Watch",
          "Short Film",
          "Vodcast",
         
        ];
      case 1:
        return ["E-Magazines", "E-Books", "E-Bookazines"];
      case 2:
        return ["Music", "Podcasts", "Audio Books"];
      case 3:
        return ["Games", "Interactive"];
      case 4:
        return ["Hamd-o-Naat", "Qawaali", "Quran Majeed",'Hajj'];  
      default:
        return [];
    }
  };

  const filteredManifestItems = manifestItems.filter((item) => {
    return item.category == getSubTabs()[subTabValue];
  });

  const handleDeleteManifestItem = async (carrierId) => {
    try {
      await client.graphql({
        query: deleteManifestItem,
        variables: { input: { id: carrierId } },
      });
      fetchItems(manifestID);
    } catch (err) {
      console.log("Error deleting carrier:", err);
    }
  };

  const columns = [
    { field: "title", headerName: "Title", flex: 1 },
    { field: "type", headerName: "Type", flex: 1 },
    { field: "category", headerName: "Category", flex: 1 },
    { field: "director", headerName: "Director", flex: 1 },
    { field: "language", headerName: "Language", flex: 1 },
    { field: "isActive", headerName: "Is Active", flex: 1, type: "boolean" },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      width: 100,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => handleOpenDialog(params.row)}>
            <VisibilityIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDeleteManifestItem(params.row.id)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <ThemeProvider theme={theme}>
        <Grid container spacing={2} style={styles.container}>
          <Typography variant="h4" style={styles.heading}>
            Manifest Items
          </Typography>

          <Grid item xs={12} style={styles.header}>
            <IconButton
              style={styles.backButton}
              onClick={handleBackClick}
              aria-label="back"
            >
              <ArrowBackIcon />
            </IconButton>{" "}
          </Grid>
          <Grid item xs={12} style={styles.addItemButton}>
            <IconButton
              style={styles.buttonAdd}
              onClick={() => handleAddItemClick(manifestId)}
            >
              <AddIcon />
            </IconButton>
            <IconButton
              style={styles.buttonDeploy}
              onClick={() => handleDeployClick()}
            >
              <DeployIcon />
            </IconButton>
          </Grid>

          <Grid item xs={12} style={styles.tabsContainer}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="content type tabs"
            >
              <Tab label="Watch" sx={styles.tab} />
              <Tab label="Read" sx={styles.tab} />
              <Tab label="Listen" sx={styles.tab} />
              <Tab label="Play" sx={styles.tab} />
              <Tab label="Devotion" sx={styles.tab} />
            </Tabs>
            <Tabs
              value={subTabValue}
              onChange={handleSubTabChange}
              aria-label="sub category tabs"
            >
              {getSubTabs().map((label, index) => (
                <Tab key={label} label={label} />
              ))}
            </Tabs>
            {getSubTabs().map((_, index) => (
              <TabPanel value={subTabValue} index={index} key={index}>
                {loading ? (
                  <Box style={styles.loadingIndicator}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid item xs={12}>
                    <DataGrid
                      rows={filteredManifestItems}
                      columns={columns}
                      pageSize={10}
                      autoHeight
                      style={styles.dataGrid}
                    />
                  </Grid>
                )}
                {!loading && filteredManifestItems.length === 0 && (
                  <p style={styles.noItemsMessage}>
                    No manifest items available.
                  </p>
                )}
              </TabPanel>
            ))}
          </Grid>
        </Grid>
      </ThemeProvider>
      {showToast && <CustomToast message={toastMessage} type={toastType} />}
    </>
  );
};

export default ManifestItems;
